class VariantOptionSelector extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.activeIndex = -1;

    /** @type {HTMLButtonElement | null} */
    this.button = null;
    /** @type {HTMLElement | null} */
    this.menu = null;
    /** @type {HTMLElement[]} */
    this.items = [];

    // Bind handlers to instance
    this.boundToggle = this.toggle.bind(this);
    this.boundOnClickOutside = this.onClickOutside.bind(this);
    this.boundOnKeyEvent = this.onKeyEvent.bind(this);
  }

  connectedCallback() {
    /** @type {HTMLButtonElement | null} */
    this.button = this.querySelector('.dropdown-button');
    /** @type {HTMLElement | null} */
    this.menu = this.querySelector('.dropdown-menu');
    /** @type {HTMLElement[]} */
    this.items = Array.from(this.querySelectorAll('.dropdown-menu-item'));

    if (this.button && this.menu && this.items.length > 0) {
      this.initEvents();
      // this.initializeDisplayedValue();
      // Do not eagerly sync prices/variants on load; Liquid handles initial state.
    }
  }

  disconnectedCallback() {
    if (this.button) {
      this.button.removeEventListener('click', this.boundToggle);
    }
    document.removeEventListener('click', this.boundOnClickOutside);
    document.removeEventListener('keydown', this.boundOnKeyEvent);
  }

  initEvents() {
    if (this.button && this.items.length > 1) {
      this.button.addEventListener('click', this.boundToggle);
    }
    document.addEventListener('click', this.boundOnClickOutside);
    document.addEventListener('keydown', this.boundOnKeyEvent);

    this.items.forEach((item, index) => {
      item.addEventListener('click', (e) => {
        e.preventDefault();
        this.selectItem(index);
      });
    });
  }

  toggle() {
    this.isOpen = !this.isOpen;
    if (this.button) {
      this.button.setAttribute('aria-expanded', this.isOpen.toString());
    }
    if (this.menu) {
      this.menu.setAttribute('aria-hidden', (!this.isOpen).toString());
    }
    this.dataset.open = this.isOpen.toString();

    if (this.isOpen) {
      this.activeIndex = -1;
    }
  }

  /**
   * Ensure the button shows the currently selected item's value (or first value)
   */
  // initializeDisplayedValue() {
  //   if (!this.button) return;
  //   const selectedItem = (this.items || []).find((el) => el.getAttribute('data-selected') === 'true') || (this.items || [])[0];
  //   const value = selectedItem ? selectedItem.getAttribute('data-value') : '';
  //   const label = this.button.querySelector('.variant-title');
  //   if (label && value) {
  //     label.textContent = value;
  //   }
  //   // For meals selector, copy price/compare price from the selected item if present
  //   if (selectedItem && this.isMealsSelector()) {
  //     this.updateButtonPricesFromMenuItem(selectedItem);
  //   }
  // }

  /**
   * @param {number} index
   */
  selectItem(index) {
    const selectedItem = (this.items || [])[index];
    if (!selectedItem) return;

    // Update selected marker on items
    this.items.forEach((item) => item.removeAttribute('data-selected'));
    selectedItem.setAttribute('data-selected', 'true');

    // Update button label
    const value = selectedItem.getAttribute('data-value') || '';
    const label = this.button ? this.button.querySelector('.variant-title') : null;
    if (label) {
      // For meals options, include the "/" separator to match the Liquid template format
      label.textContent = this.isMealsSelector() ? value + ' /' : value;
    }

    // Close dropdown and restore focus
    this.isOpen = false;
    if (this.button) {
      this.button.setAttribute('aria-expanded', 'false');
      this.button.focus();
    }
    if (this.menu) {
      this.menu.setAttribute('aria-hidden', 'true');
    }
    this.dataset.open = 'false';

    // Compute full selection across all option selectors and set matching variant on the hidden select
    this.updateVariantSelectionFromAllSelectors();

    // Update button price/compare only if this is the meals selector
    if (this.isMealsSelector()) {
      this.updateButtonPricesFromMenuItem(selectedItem) || this.updateButtonPricesFromSelect();
    }

    // Always update the meals dropdown list items using the latest other-option selection
    this.updateMealsMenuPricesForCurrentSelections();

    // If the current selector isn't meals, also refresh the meals button price from the hidden select
    if (!this.isMealsSelector()) {
      const mealsSelector = this.getAllOptionSelectorsInRoot().find((s) => s.isMealsSelector && s.isMealsSelector());
      if (mealsSelector && mealsSelector.updateButtonPricesFromSelect) {
        mealsSelector.updateButtonPricesFromSelect();
      }
    }

    // Emit events for external listeners if needed
    this.dispatchEvent(
      new CustomEvent('option-selected', {
        detail: { value, selectedIndex: index },
        bubbles: true,
      })
    );
  }

  /**
   * @param {MouseEvent} e
   */
  onClickOutside(e) {
    if (!this.isOpen) return;

    let node = /** @type {Node | null} */ (e.target);
    // Walk up DOM to determine if click happened inside this component
    while (node) {
      if (node === this) return;
      node = node.parentNode;
    }
    this.toggle();
  }

  /**
   * @param {KeyboardEvent} e
   */
  onKeyEvent(e) {
    if (!this.isOpen) return;

    if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) {
      e.preventDefault();
    }

    if (e.key === 'Tab' || e.key === 'Escape') {
      this.toggle();
      if (this.button) this.button.focus();
      return;
    }

    if (e.key === 'ArrowDown') {
      this.moveToItem(1);
    }
    if (e.key === 'ArrowUp') {
      this.moveToItem(-1);
    }
    if (e.key === 'Enter' || e.key === ' ') {
      if (this.activeIndex >= 0 && this.activeIndex < this.items.length) {
        this.selectItem(this.activeIndex);
      }
    }
  }

  /**
   * @param {number} direction +1 or -1
   */
  moveToItem(direction) {
    const total = (this.items || []).length;
    if (total === 0) return;

    let newIndex = this.activeIndex;
    newIndex = direction > 0 ? (newIndex < total - 1 ? newIndex + 1 : 0) : (newIndex > 0 ? newIndex - 1 : total - 1);
    this.activeIndex = newIndex;
    this.focusActiveItem();
  }

  focusActiveItem() {
    (this.items || []).forEach((item) => item.setAttribute('tabindex', '-1'));
    if (this.activeIndex >= 0 && this.activeIndex < (this.items || []).length) {
      const activeItem = (this.items || [])[this.activeIndex];
      if (activeItem) {
        activeItem.setAttribute('tabindex', '0');
        if (typeof activeItem.focus === 'function') activeItem.focus();
      }
    }
  }

  /**
   * Finds the closest product card wrapper for scoping queries
   * @returns {HTMLElement | null}
   */
  getRootContainer() {
    return this.closest('.product-card-variant-cta-wrapper');
  }

  /**
   * Returns all option selector components within the same product card in DOM order
   * @returns {VariantOptionSelector[]}
   */
  getAllOptionSelectorsInRoot() {
    const root = this.getRootContainer();
    if (!root) return [];
    return Array.from(root.querySelectorAll('variant-option-selector'));
  }

  /** Determine if this selector represents the meals option */
  isMealsSelector() {
    const name = (this.dataset?.optionName || '').toString().toLowerCase();
    const MEAL_OPTION_KEYS = ['meal', 'meals', 'maaltijd', 'maaltijden', 'number-of-meals', 'aantal-maaltijden'];
    return MEAL_OPTION_KEYS.some((k) => name.includes(k));
  }

  /** @returns {HTMLSelectElement | null} */
  getHiddenVariantSelect() {
    const root = this.getRootContainer();
    if (!root) return null;
    /** @type {HTMLSelectElement | null} */
    const selectEl = root.querySelector('select.product-variant-selector');
    return selectEl;
  }

  /** @returns {HTMLOptionElement | null} */
  getCurrentlySelectedVariantOption() {
    /** @type {HTMLSelectElement | null} */
    const selectEl = this.getHiddenVariantSelect();
    if (!selectEl) return null;
    const selectedIndex = selectEl.selectedIndex >= 0 ? selectEl.selectedIndex : 0;
    return selectEl.options[selectedIndex] || null;
  }

  /**
   * @param {number} cents
   */
  formatMoney(cents) {
    const amount = (Number.isFinite(cents) ? cents : 0) / 100;
    return '€' + amount.toFixed(2).replace('.', ',');
  }

  /** Ensure price/compare elements exist in the button and return them */
  ensureButtonPriceElements() {
    if (!this.button || !this.isMealsSelector()) return { priceEl: null, compareEl: null };
    const labelContainer = this.button.querySelector('.variant-title')?.closest('div');
    if (!labelContainer) return { priceEl: null, compareEl: null };

    let priceEl = this.button.querySelector('.variant-price');
    let compareEl = this.button.querySelector('.variant-compare-price');
    let priceWrapper = priceEl ? priceEl.parentElement : null;
    if (!priceWrapper) {
      priceWrapper = document.createElement('div');
      priceWrapper.className = 'flex align-center gap-4xs';
      labelContainer.appendChild(priceWrapper);
    }

    if (!priceEl) {
      priceEl = document.createElement('span');
      priceEl.className = 'variant-price text-black';
      priceWrapper.appendChild(priceEl);
    }

    if (!compareEl) {
      compareEl = document.createElement('span');
      compareEl.className = 'variant-compare-price text-grey-500 line-through text-xxs';
      priceWrapper.appendChild(compareEl);
    }

    return { priceEl, compareEl };
  }

  /**
   * Try to copy price text from the selected menu item if it exists
   * @param {HTMLElement} selectedItem
   * @returns {boolean} true if updated from menu item
   */
  updateButtonPricesFromMenuItem(selectedItem) {
    if (!this.button || !selectedItem || !this.isMealsSelector()) return false;
    const priceFromItem = selectedItem.querySelector('.variant-price');
    const compareFromItem = selectedItem.querySelector('.variant-compare-price');
    if (!priceFromItem && !compareFromItem) return false;

    const { priceEl, compareEl } = this.ensureButtonPriceElements();
    if (priceEl) {
      priceEl.textContent = (priceFromItem ? priceFromItem.textContent?.trim() : '') || '';
      if (compareFromItem && compareFromItem.textContent?.trim()) {
        priceEl.classList.add('text-error-700');
        priceEl.classList.remove('text-black');
      } else {
        priceEl.classList.add('text-black');
        priceEl.classList.remove('text-error-700');
      }
    }
    if (compareEl) {
      compareEl.textContent = (compareFromItem ? compareFromItem.textContent?.trim() : '') || '';
    }
    return true;
  }

  /** Read currently selected variant from hidden select and update button prices */
  updateButtonPricesFromSelect() {
    if (!this.isMealsSelector()) return false;
    const selectedOption = this.getCurrentlySelectedVariantOption();
    if (!selectedOption) return false;
    const price = parseInt(selectedOption.getAttribute('data-variant-price') || '0', 10);
    const compare = parseInt(selectedOption.getAttribute('data-variant-compare-at-price') || '0', 10);
    this.updateButtonPricesFromVariantNumbers(price, compare);
    return true;
  }

  /**
   * Update button price/compare using raw cents numbers
   * @param {number} priceCents
   * @param {number} compareCents
   */
  updateButtonPricesFromVariantNumbers(priceCents, compareCents) {
    if (!this.button || !this.isMealsSelector()) return;
    const { priceEl, compareEl } = this.ensureButtonPriceElements();
    if (priceEl) {
      const isDiscount = Number.isFinite(compareCents) && compareCents > priceCents && compareCents > 0;
      priceEl.textContent = this.formatMoney(priceCents);
      priceEl.classList.toggle('text-error-700', !!isDiscount);
      priceEl.classList.toggle('text-black', !isDiscount);
    }
    if (compareEl) {
      compareEl.textContent = Number.isFinite(compareCents) && compareCents > priceCents && compareCents > 0
        ? this.formatMoney(compareCents)
        : '';
    }
  }

  /** Update all items in the meals dropdown to reflect prices for current selection of other options */
  updateMealsMenuPricesForCurrentSelections() {
    const selectors = this.getAllOptionSelectorsInRoot();
    if (selectors.length === 0) return;
    const mealsSelector = selectors.find((s) => s.isMealsSelector && s.isMealsSelector());
    if (!mealsSelector) return;
    const mealsIndex = selectors.indexOf(mealsSelector);
    if (mealsIndex < 0) return;

    /** @type {HTMLSelectElement | null} */
    const selectEl = this.getHiddenVariantSelect();
    if (!selectEl) return;

    const selectedValues = selectors.map((sel) => VariantOptionSelector.getSelectedValueForSelector(sel));
    const optionEls = Array.from(selectEl.querySelectorAll('option'));

    /**
     * @param {string[]} values
     */
    const findOptionForValues = (values) => {
      const title = values.join(' / ');
      let match = optionEls.find((opt) => (opt.getAttribute('data-variant-title') || '').trim() === title);
      if (!match) {
        match = optionEls.find((opt) => {
          const t = (opt.getAttribute('data-variant-title') || '').trim();
          return t.split(' / ').every((part, idx) => part === values[idx]);
        });
      }
      return match || null;
    };

    (mealsSelector.items || []).forEach((item) => {
      const itemValue = item.getAttribute('data-value') || '';
      const values = selectedValues.slice();
      values[mealsIndex] = itemValue;
      const match = findOptionForValues(values);
      if (!match) return;
      const price = parseInt(match.getAttribute('data-variant-price') || '0', 10);
      const compare = parseInt(match.getAttribute('data-variant-compare-at-price') || '0', 10);

      const priceEl = item.querySelector('.variant-price');
      if (priceEl) {
        const isDiscount = compare > price && compare > 0;
        priceEl.textContent = this.formatMoney(price);
        priceEl.classList.toggle('text-error-700', !!isDiscount);
        priceEl.classList.toggle('text-black', !isDiscount);
      }
      const compareEl = item.querySelector('.variant-compare-price');
      if (compareEl) {
        compareEl.textContent = compare > price && compare > 0 ? this.formatMoney(compare) : '';
      }
    });
  }

  /**
   * Determines the selected value for a given selector element
   * @param {VariantOptionSelector} selector
   * @returns {string}
   */
  static getSelectedValueForSelector(selector) {
    const items = selector.items || [];
    const selectedItem = items.find((el) => el.getAttribute('data-selected') === 'true') || items[0];
    if (selectedItem) {
      return selectedItem.getAttribute('data-value') || '';
    }
    // Fallback to button label if items not available, but clean it up
    const button = selector.querySelector('.dropdown-button .variant-title');
    return button ? button.textContent || '' : '';

    // if (button && button.textContent) {
    //   // Remove the trailing " /" if it's a meals selector
    //   let text = button.textContent.trim();
    //   if (text.endsWith(' /')) {
    //     text = text.slice(0, -2).trim();
    //   }
    //   return text;
    // }
    // // Final fallback to first item value
    // return items[0] ? items[0].getAttribute('data-value') || '' : '';
  }

  /**
   * Compute combined option values and set corresponding variant on the hidden select
   */
  updateVariantSelectionFromAllSelectors() {
    const root = this.getRootContainer();
    if (!root) return;

    /** @type {HTMLSelectElement | null} */
    const selectEl = root.querySelector('select.product-variant-selector');
    if (!selectEl) return;

    const selectors = this.getAllOptionSelectorsInRoot();
    if (selectors.length === 0) return;

    // Build selected values and detect meals option in a single pass
    const MEAL_OPTION_KEYS = ['meal', 'meals', 'maaltijd', 'maaltijden', 'number-of-meals', 'aantal-maaltijden'];
    /** @type {string[]} */
    const selectedValues = [];
    let mealsValue = '';
    for (const sel of selectors) {
      const value = VariantOptionSelector.getSelectedValueForSelector(sel);
      selectedValues.push(value);
      if (!mealsValue) {
        const name = (sel.dataset?.optionName || '').toString().toLowerCase();
        if (MEAL_OPTION_KEYS.some((k) => name.includes(k))) {
          mealsValue = value || '';
        }
      }
    }
    const combinedTitle = selectedValues.join(' / ');

    /** @type {HTMLOptionElement[]}
     */
    const optionEls = Array.from(selectEl.querySelectorAll('option'));

    // Prefer exact match by full variant title
    let match = optionEls.find((opt) => (opt.getAttribute('data-variant-title') || '').trim() === combinedTitle);
    // Fallback: match by containing ordered values
    if (!match) {
      match = optionEls.find((opt) => {
        const title = (opt.getAttribute('data-variant-title') || '').trim();
        return title.split(' / ').every((part, idx) => part === selectedValues[idx]);
      });
    }
    if (match) {
      const variantId = match.value;
      const variantPrice = parseInt(match.getAttribute('data-variant-price') || '0', 10);
      const variantCompareAtPrice = parseInt(match.getAttribute('data-variant-compare-at-price') || '0', 10);
      const variantPoints = match.getAttribute('data-variant-points');
      selectEl.value = variantId;
      
      // Fallback: if meals option name wasn't detected, infer from values
      if (!mealsValue) {
        mealsValue = (selectedValues.find((v) => {
          const s = (v || '').toString().toLowerCase();
          return MEAL_OPTION_KEYS.some((k) => s.includes(k));
        }) || '').toString();
      }

      const numMatch = mealsValue.match(/\d+/);
      const numberOfMeals = numMatch ? parseInt(numMatch[0], 10) : 0;
      if (numberOfMeals > 0 && variantPrice > 0) {
        const mealPrice = (variantPrice / numberOfMeals) / 100; // cents to currency
        const productCardEl = this.closest('.product-card');
        const priceElement = productCardEl ? productCardEl.querySelector('.custom-product-card-price') : null;
        if (priceElement) {
          priceElement.textContent = '€' + mealPrice.toFixed(2).replace('.', ',');
        }
      }

      if(variantPoints && variantPoints != '') {
        const pointsTextElement = document.querySelector('.product-points-number');
        if(pointsTextElement) {
          pointsTextElement.textContent = `${parseInt(variantPoints)}`;
        }
      }

      // Update any id input inside the same card form
      /** @type {HTMLInputElement | null} */
      const idInput = root.querySelector('input[name="id"]');
      if (idInput) idInput.value = variantId;

      // Notify listeners that selection changed
      selectEl.dispatchEvent(new Event('change', { bubbles: true }));
      this.dispatchEvent(
        new CustomEvent('variant-changed', {
          detail: {
            variantId,
            title: match.getAttribute('data-variant-title') || '',
            selectedValues,
            optionIndex: Number(this.dataset.optionIndex || 0),
            optionName: this.dataset.optionName || '',
          },
          bubbles: true,
        })
      );

      // Also update the button's price/compare display using this variant data when available, but only for meals selector
      if (this.isMealsSelector()) {
        this.updateButtonPricesFromVariantNumbers(variantPrice, variantCompareAtPrice);
      }
    }
  }
}

customElements.define('variant-option-selector', VariantOptionSelector);